"use client";

import { slugify } from "#utils/utils";
import {
  Box,
  Divider,
  Heading,
  HStack,
  Image,
  Link,
  Text,
  useColorModeValue,
  VStack,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import React from "react";

const MotionBox = motion(Box);

interface CaseStudyCardProps {
  slug?: string;
  title: string;
  imgSrc: string;
  companyDesc: string;
  companyName: string;
  summary: string;
  uri: string;
}

export const CaseStudyCard: React.FC<CaseStudyCardProps> = ({
  slug,
  title,
  imgSrc,
  companyDesc,
  companyName,
  summary,
}) => {
  const bg = useColorModeValue("blackAlpha.100", "whiteAlpha.100");
  const bgLine = useColorModeValue(
    "colors.brand.primary.900",
    "colors.brand.primary.100"
  );

  return (
    <MotionBox
      bg={bg}
      p={6}
      rounded="lg"
      shadow="lg"
      id={slugify(companyName)}
      whileHover={{ scale: 1.05 }}
      transition={{ type: "spring", stiffness: 500 }}
    >
      <VStack spacing={3} align="start" h="100%">
        <HStack spacing={3} w="full">
          <Box h={12} minW={12} overflow="hidden" rounded="md" bg="white" p={1} border="1px solid" borderColor={bgLine}>
            <Image
              src={imgSrc}
              fallbackSrc={"/img/clients/default.webp"}
              fallbackStrategy="onError"
              alt={`${companyName} logo`}
              objectFit="contain"
              h="100%"
              w="100%"
            />
          </Box>
          <VStack align="start" spacing={0}>
            <Heading fontSize="lg" as={"h2"} lineHeight="tight">
              {companyName}
            </Heading>
            <Text fontSize="xs" color="gray.500" noOfLines={1}>
              {companyDesc}
            </Text>
          </VStack>
        </HStack>

        <Divider my={2} bgColor={bgLine} />

        <Heading fontSize="md" as={"h3"} color="blue.500">
          {title}
        </Heading>

        <Text fontSize="sm" color={useColorModeValue("gray.700", "gray.300")} noOfLines={3} flex="1">
          {summary.replace(/[*#>`-]/g, '').substring(0, 150)}...
        </Text>

        <Box mt="auto" w="full" pt={4}>
          <Link href={`/case-studies/${slug}`} _hover={{ textDecoration: 'none' }}>
            <Text fontWeight="bold" color="blue.500" fontSize="sm" display="flex" alignItems="center" _hover={{ color: "blue.600" }}>
              Read Full Impact Study <Box as="span" ml={1}>→</Box>
            </Text>
          </Link>
        </Box>
      </VStack>
    </MotionBox>
  );
};
